/**
 * Account API - profile, update profile, change email/password, delete account.
 * Uses getApiBaseUrl from api.js and Bearer token for auth.
 */

import { getApiBaseUrl, getBaseHeaders } from './api';

/**
 * @param {{ accessToken?: string }} options
 * @returns {Headers}
 */
function getHeaders({ accessToken } = {}) {
    const headers = { ...getBaseHeaders() };
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return headers;
}

/**
 * GET /api/users/me - get current user profile
 * @param {{ accessToken?: string }} options
 * @returns {Promise<{ ok: boolean, data?: { user }, error?: string }>}
 */
export async function getAccountProfile({ accessToken } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/users/me`, {
        method: 'GET',
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * POST /api/users/me/avatar - upload avatar image (multipart), store on server, save path in DB
 * @param {{ accessToken: string, file: File, onProgress?: (percent: number) => void }} options
 * @returns {Promise<{ ok: boolean, data?: { user }, error?: string }>}
 */
export function uploadAvatar({ accessToken, file, onProgress } = {}) {
    const base = getApiBaseUrl();
    const url = `${base}/api/users/me/avatar`;
    const formData = new FormData();
    formData.append('avatar', file);

    return new Promise((resolve) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url);
        if (accessToken) {
            xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
        }

        xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable && typeof onProgress === 'function') {
                const percent = Math.round((e.loaded / e.total) * 100);
                onProgress(percent);
            }
        });

        xhr.addEventListener('load', () => {
            let json = {};
            try {
                json = JSON.parse(xhr.responseText || '{}');
            } catch (_) {}
            if (xhr.status >= 200 && xhr.status < 300 && json.success === true && json.data) {
                resolve({ ok: true, data: json.data });
            } else {
                resolve({
                    ok: false,
                    error: json?.error?.message || `Request failed (${xhr.status})`,
                });
            }
        });

        xhr.addEventListener('error', () => {
            resolve({ ok: false, error: 'Network error' });
        });

        xhr.send(formData);
    });
}

/**
 * PATCH /api/users/me - update profile (name and/or avatar)
 * @param {{ accessToken?: string, name?: string, avatar?: string | null }} options
 * @returns {Promise<{ ok: boolean, data?: { user }, error?: string }>}
 */
export async function updateProfile({ accessToken, name, avatar } = {}) {
    const base = getApiBaseUrl();
    const body = {};
    if (name !== undefined) body.name = name;
    if (avatar !== undefined) body.avatar = avatar;
    const res = await fetch(`${base}/api/users/me`, {
        method: 'PATCH',
        headers: getHeaders({ accessToken }),
        body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * POST /api/account/change-email
 * @param {{ accessToken?: string, newEmail: string, currentPassword: string }} options
 * @returns {Promise<{ ok: boolean, data?: { user }, error?: string }>}
 */
export async function changeEmail({ accessToken, newEmail, currentPassword } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/account/change-email`, {
        method: 'POST',
        headers: getHeaders({ accessToken }),
        body: JSON.stringify({ newEmail, currentPassword }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * POST /api/account/change-password
 * @param {{ accessToken?: string, currentPassword: string, newPassword: string }} options
 * @returns {Promise<{ ok: boolean, data?: { message }, error?: string }>}
 */
export async function changePassword({ accessToken, currentPassword, newPassword } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/account/change-password`, {
        method: 'POST',
        headers: getHeaders({ accessToken }),
        body: JSON.stringify({ currentPassword, newPassword }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * DELETE /api/account
 * @param {{ accessToken?: string, password: string }} options
 * @returns {Promise<{ ok: boolean, data?: { message }, error?: string }>}
 */
export async function deleteAccount({ accessToken, password } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/account`, {
        method: 'DELETE',
        headers: getHeaders({ accessToken }),
        body: JSON.stringify({ password }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}
